import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';

import { authService } from '../services/authService';

const schema = z.object({
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

type FormData = z.infer<typeof schema>;

interface SignInFormProps {
  onSuccess?: () => void;
}

export function SignInForm({ onSuccess }: SignInFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    setServerError('');
    const result = await authService.signInWithPassword(data.email, data.password);
    setIsLoading(false);
    if (!result.ok) {
      setServerError(result.message);
      return;
    }
    onSuccess?.();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
      <div className="flex flex-col gap-1">
        <label htmlFor="email" className="text-small font-medium text-ink-700">
          Email address
        </label>
        <div className="relative">
          <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300 pointer-events-none" />
          <input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.co.uk"
            {...register('email')}
            className="w-full h-10 pl-9 pr-3 border border-ink-100 rounded-md text-body text-ink-900 placeholder:text-ink-300 focus:border-violet-400 focus:ring-1 focus:ring-violet-400 outline-none transition-colors"
          />
        </div>
        {errors.email && (
          <p className="text-caption text-danger-400">{errors.email.message}</p>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="password" className="text-small font-medium text-ink-700">
          Password
        </label>
        <div className="relative">
          <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300 pointer-events-none" />
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="••••••••"
            {...register('password')}
            className="w-full h-10 pl-9 pr-10 border border-ink-100 rounded-md text-body text-ink-900 placeholder:text-ink-300 focus:border-violet-400 focus:ring-1 focus:ring-violet-400 outline-none transition-colors"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-300 hover:text-ink-700 transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.password && (
          <p className="text-caption text-danger-400">{errors.password.message}</p>
        )}
      </div>

      {serverError && (
        <p className="text-small text-danger-400 bg-danger-bg px-3 py-2 rounded-sm">
          {serverError}
        </p>
      )}

      <button
        type="submit"
        disabled={isLoading}
        className="w-full h-11 bg-midnight text-paper rounded-md font-medium text-small hover:opacity-90 active:opacity-80 disabled:opacity-50 transition-opacity"
      >
        {isLoading ? 'Signing in…' : 'Sign in'}
      </button>
    </form>
  );
}
